import React, { useCallback, useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "./ui/alert-dialog";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Plus, Trash2, Pencil, ImagePlus, ImageOff } from "lucide-react";

function compressImage(file, maxW = 1600, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMPTY = { title: "", subtitle: "", image: "", link: "", cta_label: "Apply Now", active: true, order: 0 };

function OfferForm({ initial, onSave }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    setUploading(true);
    try {
      const dataUrl = await compressImage(file);
      setForm((f) => ({ ...f, image: dataUrl }));
    } catch { toast.error("Could not process image."); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Banner Image</Label>
        <div className="flex items-center gap-4">
          <div className="flex h-24 w-40 items-center justify-center overflow-hidden rounded-lg border bg-secondary/40">
            {form.image ? <img src={form.image} alt="preview" className="h-full w-full object-cover" />
              : <ImageOff className="h-6 w-6 text-muted-foreground" />}
          </div>
          <div>
            <input id="offer-file" type="file" accept="image/*" className="hidden" onChange={onFile} />
            <Button type="button" variant="outline" onClick={() => document.getElementById("offer-file").click()} disabled={uploading}>
              <ImagePlus className="mr-1.5 h-4 w-4" />{uploading ? "Processing…" : "Upload Image"}
            </Button>
            <p className="mt-1 text-xs text-muted-foreground">JPG/PNG. Auto-optimised for fast loading.</p>
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5 sm:col-span-2"><Label>Title</Label>
          <Input value={form.title} onChange={set("title")} placeholder="Offer headline" /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Subtitle</Label>
          <Textarea rows={2} value={form.subtitle} onChange={set("subtitle")} placeholder="Short supporting line" /></div>
        <div className="space-y-1.5"><Label>Button Label</Label>
          <Input value={form.cta_label} onChange={set("cta_label")} placeholder="Apply Now" /></div>
        <div className="space-y-1.5"><Label>Link (path or URL)</Label>
          <Input value={form.link} onChange={set("link")} placeholder="/loans/personal-loan" /></div>
        <div className="space-y-1.5"><Label>Display Order</Label>
          <Input type="number" value={form.order} onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))} /></div>
        <div className="flex items-center gap-3 pt-6">
          <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} />
          <Label className="cursor-pointer">Active (show on site)</Label>
        </div>
      </div>
      <DialogFooter>
        <Button onClick={() => {
          if (!form.title && !form.image) return toast.error("Add a title or image.");
          onSave(form);
        }}>Save Offer</Button>
      </DialogFooter>
    </div>
  );
}

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editOffer, setEditOffer] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/offers"); setOffers(data); }
    catch { toast.error("Failed to load offers"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (form) => {
    try { await api.post("/admin/offers", form); toast.success("Offer added."); setAddOpen(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
  };
  const update = async (form) => {
    try { await api.patch(`/admin/offers/${editOffer.id}`, form); toast.success("Offer updated."); setEditOffer(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const toggle = async (o) => {
    try { await api.patch(`/admin/offers/${o.id}`, { active: !o.active }); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (o) => {
    try { await api.delete(`/admin/offers/${o.id}`); toast.success("Offer deleted."); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <Card className="border">
      <div className="flex items-center justify-between border-b p-4">
        <div>
          <h2 className="font-heading text-lg font-bold">Homepage Offer Slider</h2>
          <p className="text-sm text-muted-foreground">Upload and manage promotional banners shown on the homepage anytime.</p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild><Button><Plus className="mr-1.5 h-4 w-4" />Add Offer</Button></DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
            <DialogHeader><DialogTitle>Add Offer</DialogTitle>
              <DialogDescription>Create a promotional slide for the homepage.</DialogDescription></DialogHeader>
            <OfferForm onSave={create} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="p-4">
        {loading ? (
          <p className="py-12 text-center text-muted-foreground">Loading…</p>
        ) : offers.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground">No offers yet. The homepage will show default slides until you add some.</p>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {offers.map((o) => (
              <Card key={o.id} className="overflow-hidden border">
                <div className="relative h-32 bg-secondary/40">
                  {o.image ? <img src={o.image} alt={o.title} className="h-full w-full object-cover" />
                    : <div className="flex h-full items-center justify-center"><ImageOff className="h-6 w-6 text-muted-foreground" /></div>}
                  <Badge className={`absolute right-2 top-2 ${o.active ? "bg-brand-green" : "bg-muted-foreground"}`}>
                    {o.active ? "Active" : "Hidden"}
                  </Badge>
                </div>
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Order: {o.order}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Show</span>
                      <Switch checked={o.active} onCheckedChange={() => toggle(o)} />
                    </div>
                  </div>
                  <h3 className="mt-2 font-heading text-base font-semibold line-clamp-1">{o.title || "(no title)"}</h3>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{o.subtitle}</p>
                  <div className="mt-3 flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => setEditOffer(o)}><Pencil className="mr-1.5 h-3.5 w-3.5" />Edit</Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="outline" size="sm" className="text-destructive hover:text-destructive"><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete</Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Delete this offer?</AlertDialogTitle>
                          <AlertDialogDescription>It will be removed from the homepage slider.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => remove(o)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!editOffer} onOpenChange={(v) => !v && setEditOffer(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle>Edit Offer</DialogTitle>
            <DialogDescription>Update this promotional slide.</DialogDescription></DialogHeader>
          {editOffer && <OfferForm initial={editOffer} onSave={update} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
